<template>
  <div class="flex-1 overflow-y-auto">
    <div v-if="loading" class="flex items-center justify-center h-64">
      <div class="text-gray-500">{{ $t('translation.loading.text') }}</div>
    </div>

    <div v-else class="space-y-3 p-3 sm:p-4">
      <div
        v-for="key in paginatedKeys"
        :key="key.id"
        class="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-white"
      >
        <!-- Header de la clé -->
        <div
          @click="$emit('toggleExpansion', key.id)"
          class="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 hover:bg-gray-100 cursor-pointer flex items-center justify-between gap-2 sm:gap-4"
        >
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 sm:gap-3">
              <ChevronDownIcon
                :class="[
                  'h-4 w-4 text-gray-500 transition-transform flex-shrink-0',
                  expandedKeys.has(key.id) ? 'rotate-180' : '',
                ]"
              />
              <div class="min-w-0 flex-1">
                <h3 class="font-medium text-gray-900 text-sm truncate">{{ key.keyPath }}</h3>
                <p v-if="key.description" class="text-xs text-gray-600 mt-1 line-clamp-1">
                  {{ key.description }}
                </p>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-2 flex-shrink-0">
            <span class="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded whitespace-nowrap">
              {{ key.context }}
            </span>
            <div class="flex gap-1">
              <span
                v-for="lang in availableLanguages"
                :key="lang.code"
                :class="[
                  'w-2 h-2 rounded-full flex-shrink-0',
                  getTranslation(key.id, lang.code) ? 'bg-green-500' : 'bg-red-300',
                ]"
                :title="`${lang.name}: ${getTranslation(key.id, lang.code) ? $t('translation.key.translated') : $t('translation.key.missing')}`"
              ></span>
            </div>
          </div>
        </div>

        <!-- Contenu expandable (traductions) -->
        <div v-if="expandedKeys.has(key.id)" class="border-t border-gray-200 bg-white">
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-3 sm:p-4">
            <div
              v-for="lang in availableLanguages"
              :key="`${key.id}-${lang.code}`"
              class="border border-gray-200 rounded-md p-3 bg-gray-50"
            >
              <div class="flex items-center justify-between mb-2">
                <label class="text-sm font-medium text-gray-700 flex items-center">
                  <span class="text-base mr-2">{{ lang.flag }}</span>
                  {{ lang.name }}
                </label>
                <span
                  v-if="getTranslation(key.id, lang.code)"
                  class="text-xs text-green-600 bg-green-100 px-2 py-1 rounded"
                >
                  ✓
                </span>
                <span v-else class="text-xs text-red-600 bg-red-100 px-2 py-1 rounded"> ✗ </span>
              </div>

              <textarea
                :value="editableValues[`${key.id}-${lang.code}`]"
                @input="
                  $emit(
                    'updateTranslation',
                    key.id,
                    lang.code,
                    ($event.target as HTMLTextAreaElement).value
                  )
                "
                :placeholder="$t('translation.key.placeholder', { lang: lang.name.toLowerCase() })"
                rows="2"
                class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
              ></textarea>

              <div class="flex justify-end mt-2">
                <button
                  @click="$emit('saveTranslation', key.id, lang.code)"
                  :disabled="!hasUnsavedChanges(key.id, lang.code)"
                  class="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {{ $t('translation.key.save') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Language, Translation, TranslationKey } from '@/composables/useTranslationManager'
import { ChevronDownIcon } from 'lucide-vue-next'

interface Props {
  loading: boolean
  paginatedKeys: TranslationKey[]
  expandedKeys: Set<number>
  availableLanguages: Language[]
  editableValues: Record<string, string>
  getTranslation: (keyId: number, languageCode: string) => Translation | undefined
  hasUnsavedChanges: (keyId: number, languageCode: string) => boolean
}

defineProps<Props>()

defineEmits<{
  toggleExpansion: [keyId: number]
  updateTranslation: [keyId: number, languageCode: string, value: string]
  saveTranslation: [keyId: number, languageCode: string]
}>()
</script>
